import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  IconButton,
  Button,
  ListItem,
  TextField,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import DeleteIcon from '@material-ui/icons/Delete'
import SaveIcon from '@material-ui/icons/Save'
import UploadIcon from '@material-ui/icons/CloudUpload'
import AddIcon from '@material-ui/icons/Add'
import { List, makeStyles } from '@material-ui/core'

import LargeToolTip from '../account/components/LargeToolTip'

const useStyles = makeStyles(theme => ({
  dialogButtonLabel: {
    marginRight: 'auto'
  },
  hidden: { 
    display: 'none !important'
  },
  heading: {
    fontSize: '25px'
  },
  floatRight: {
    float: 'right'
  },
  removeIcon: {
    color: 'red',
    '& > svg': {
      fontSize: '18px'
    }
  }
}))

function download(data, filename, type) {
  var file = new Blob([data], { type: type })
  if (window.navigator.msSaveOrOpenBlob)
    // IE10+
    window.navigator.msSaveOrOpenBlob(file, filename)
  else {
    // Others
    var a = document.createElement('a'),
      url = URL.createObjectURL(file)
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    setTimeout(function () {
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    }, 0)
  }
}

const getCsvDataFromText = (text) => {
  // split into lines and create phrases
  const lines = text.split(/\r\n|\n/)
  const phrases = []
  lines.forEach((line, i) => {
    if (i !== 0) {
      phrases.push(line)
    }
  })
  return phrases
}

const ImportDialog = (props) => {
  const { open, onClose, getPhraseErrors, vocabData, setVocabData, overwriteVocab } = props
  const [uploadedPhrases, setUploadedPhrases] = useState(null)
  const [newPhraseAdded, setNewPhraseAdded] = useState(false)

  const handleDownloadExample = () => {
    download(
      "phrase\nDarneille\nO'Ban\nOlympia\nDeBolt\nLovick\nVan De Wege\nRepresentative Rude\nRepresentative Ryu",
      'Example Vocabulary.csv',
      'text/csv'
    )
  }

  const handleUpload = (ev) => {
    const file = ev.target.files[0]
    const reader = new FileReader()
    reader.onload = (ev) => {
      const fullText = ev.target.result
      setUploadedPhrases(getCsvDataFromText(fullText))
    }
    reader.readAsText(file)
  }

  const handleUpdatePhrase = (newPhrase, index) => {
    const phrases = [...uploadedPhrases]
    phrases.splice(index, 1, newPhrase)
    setUploadedPhrases(phrases)
  }

  const handleRemovePhrase = (index) => {
    const phrases = [...uploadedPhrases]
    phrases.splice(index, 1)
    setUploadedPhrases(phrases)
  }

  const handleAddPhrase = () => {
    setUploadedPhrases([...uploadedPhrases, ''])
    setNewPhraseAdded(true)
  }

  const getUploadedPhraseErrors = (phrase, index) => {
    const otherPhrases = [...uploadedPhrases]
    otherPhrases.splice(index, 1) //remove this phrase from the other phrases array
    const phraseErrors = getPhraseErrors([...vocabData, ...otherPhrases], phrase)
    return phraseErrors
  }

  const validPhrases =
    (uploadedPhrases &&
      uploadedPhrases.filter(
        (uploadedPhrase, i) => !getUploadedPhraseErrors(uploadedPhrase, i).length
      )) ||
    []

  const handleSave = () => {
    onClose()
    setVocabData([...vocabData, ...validPhrases])
    overwriteVocab([...vocabData, ...validPhrases])
  }

  const classes = useStyles()
  return (
    <Dialog onClose={onClose} open={open} maxWidth='md' fullWidth>
      <DialogTitle>
        <span className={classes.heading}>Upload Vocabulary</span>
        <IconButton className={classes.floatRight} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {uploadedPhrases === null ? (
          <>
            You may upload a .csv file with the vocabulary. The file should be
            formatted so that the first line is a header "phrase" and the
            following lines are the phrases (See the example vocabulary file
            below).
          </>
        ) : (
          <List>
            {uploadedPhrases.map((phrase, i) => {
              const phraseErrors = getUploadedPhraseErrors(phrase, i)
              return (
                <LargeToolTip
                  key={`phrase ${i} tooltip`}
                  tooltipclass={{ whiteSpace: 'pre', maxWidth: 'none' }}
                  title={phraseErrors.join(',\n')}
                >
                  <ListItem key={`phrase ${i} listItem`}>
                    <TextField
                      inputProps={{ style: { fontSize: '16px' } }}
                      autoFocus={newPhraseAdded}
                      key={`phrase ${i} textfield`}
                      value={phrase}
                      fullWidth
                      onChange={(ev) => handleUpdatePhrase(ev.target.value, i)}
                      value={uploadedPhrases[i]}
                      variant='outlined'
                      placeholder='phrase'
                      error={!!phraseErrors.length}
                    />
                    <IconButton
                      className={classes.removeIcon}
                      onClick={() => handleRemovePhrase(i)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItem>
                </LargeToolTip>
              )
            })}
            <Button
              variant='contained'
              startIcon={<AddIcon />}
              onClick={handleAddPhrase}
            >
              New Phrase
            </Button>
          </List>
        )}
      </DialogContent>
      <ImportDialogActions
        handleDownloadExample={handleDownloadExample}
        handleUpload={handleUpload}
        handleSave={handleSave}
        hasUploadedPhrases={!!uploadedPhrases}
        numValidPhrases={validPhrases.length}
      />
    </Dialog>
  )
}

const ImportDialogActions = props => {
  const {
    handleDownloadExample,
    handleUpload,
    handleSave,
    hasUploadedPhrases,
    numValidPhrases,
  } = props
  const classes = useStyles()
  return (
    <DialogActions>
      <label className={classes.dialogButtonLabel}>
        <Button onClick={handleDownloadExample} variant='contained'>
          Example Vocabulary.csv
        </Button>
      </label>
      <input
        onChange={handleUpload}
        type='file'
        accept='text/csv'
        id='vocab-csv-upload'
        className={classes.hidden}
      />
      <label htmlFor='vocab-csv-upload'>
        <Button
          component='span'
          variant='contained'
          color={hasUploadedPhrases ? 'default' : 'primary'}
          startIcon={<UploadIcon />}
        >
          {hasUploadedPhrases ? 'Replace Upload' : 'Upload'}
        </Button>
      </label>
      <label>
        <Button
          disabled={!hasUploadedPhrases || numValidPhrases === 0}
          title='Phases will be automatically saved once added'
          onClick={handleSave}
          variant='contained'
          color='primary'
          startIcon={<SaveIcon />}
        >
          Add {numValidPhrases} Phrases
        </Button>
      </label>
    </DialogActions>
  )
}

export default ImportDialog
